import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../utils/api'
import styles from './VaultAccessPage.module.css'

interface VaultDocument {
  id: string
  type: string
  name: string
  period_year: number | null
  period_month: number | null
  size_bytes: number
  created_at: string
}

export function VaultAccessPage() {
  const { t } = useTranslation()
  const [employeeId, setEmployeeId] = useState('')
  const [accessCode, setAccessCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [vaultToken, setVaultToken] = useState<string | null>(null)
  const [documents, setDocuments] = useState<VaultDocument[]>([])

  const handleAccess = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSubmitting(true)

    const res = await apiFetch<{ vaultToken: string }>('/vault/access', {
      method: 'POST',
      body: JSON.stringify({ employeeId, accessCode }),
    })

    if (res.error) {
      setError(res.error)
      setSubmitting(false)
      return
    }

    if (res.data) {
      setVaultToken(res.data.vaultToken)
      // Fetch documents
      const docsRes = await apiFetch<{ documents: VaultDocument[] }>(
        `/vault/documents?token=${res.data.vaultToken}`,
      )
      if (docsRes.data) {
        setDocuments(docsRes.data.documents)
      }
    }
    setSubmitting(false)
  }

  const formatSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const getTypeLabel = (type: string): string => {
    const key = `vault.type_${type}` as const
    const label = t(key)
    return label === key ? type : label
  }

  const formatPeriod = (year: number | null, month: number | null): string => {
    if (!year || !month) return ''
    return `${String(month).padStart(2, '0')}/${year}`
  }

  // ─── Login form ────────────────────────────────────────────

  if (!vaultToken) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className={styles.logo}>MIREY</div>
          <div className={styles.vaultIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              <circle cx="12" cy="16" r="1" />
            </svg>
          </div>
          <h2 className={styles.title}>{t('vault.title')}</h2>
          <p className={styles.subtitle}>{t('vault.subtitle')}</p>

          {error && <div className={styles.error}>{error}</div>}

          <form className={styles.form} onSubmit={handleAccess}>
            <label className={styles.label}>
              {t('verify.employee')}
              <input
                type="text"
                className={styles.input}
                value={employeeId}
                onChange={e => setEmployeeId(e.target.value)}
                required
                disabled={submitting}
                placeholder="ID"
              />
            </label>

            <label className={styles.label}>
              {t('vault.access_code')}
              <input
                type="password"
                className={styles.input}
                value={accessCode}
                onChange={e => setAccessCode(e.target.value)}
                required
                disabled={submitting}
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
              />
            </label>

            <button type="submit" className={styles.button} disabled={submitting}>
              {submitting ? t('common.loading') : t('vault.access_button')}
            </button>
          </form>
        </div>
      </div>
    )
  }

  // ─── Document list ─────────────────────────────────────────

  return (
    <div className={styles.page}>
      <div className={styles.vaultPanel}>
        <div className={styles.vaultHeader}>
          <div className={styles.logo}>MIREY</div>
          <h2 className={styles.title}>{t('vault.title')}</h2>
        </div>

        {documents.length === 0 ? (
          <div className={styles.empty}>
            <p>{t('vault.no_documents')}</p>
          </div>
        ) : (
          <div className={styles.docList}>
            {documents.map(doc => (
              <div key={doc.id} className={styles.docRow}>
                <div className={styles.docInfo}>
                  <span className={styles.docType}>{getTypeLabel(doc.type)}</span>
                  <span className={styles.docName}>{doc.name}</span>
                  {doc.period_year && (
                    <span className={styles.docPeriod}>
                      {formatPeriod(doc.period_year, doc.period_month)}
                    </span>
                  )}
                </div>
                <div className={styles.docMeta}>
                  <span className={styles.docSize}>{formatSize(doc.size_bytes)}</span>
                  <button className={styles.downloadBtn}>
                    {t('vault.download')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
